import React from 'react';
import PropTypes from 'prop-types';
import {
  withStyles,
  MuiThemeProvider,
  createMuiTheme
} from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import green from '@material-ui/core/colors/green';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import CheckTwoToneIcon from '@material-ui/icons/CheckTwoTone';
import WarningIcon from '@material-ui/icons/WarningRounded';
import TextField from '@material-ui/core/TextField';
import LoadingEllipsis from './shared/loading-ellipsis/LoadingEllipsis';
import axios from 'axios';
import styles from '../styles/common';

const themeDark = createMuiTheme({
  palette: {
    background: {
      default: '#444'
    }
  }
});

const UpdateButton = withStyles(() => ({
  root: {
    color: '#FFFFFF',
    backgroundColor: '#FF6800',
    float: 'right'
  }
}))(Button);

const CancelButton = withStyles(() => ({
  root: {
    border: '1px solid #9B9B9B',
    borderRadius: '4.5px',
    color: '#9B9B9B',
    backgroundColor: '#FFFFFF'
  }
}))(Button);

class ProfileUpdate extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      userData: {},
      newUserData: {},
      postError: false,
      getError: false,
      errorResponse: '',
      userFound: false,
      orgAddress: {},
      profileId: '',
      cardContentText: 'Your profile information has been updated.',
      disableCardcontent: false,
      updateDisabled: true,
      formErrors: {}
    };
  }

  async componentDidMount() {
    this.prepareProfile();
  }

  async prepareProfile() {
    let profile = this.props.profile;
    const params = { id: profile };
    const userData = await axios('/profile_update/get_contact_info', {
      params: params
    }).catch(error => {
      this.setState({
        getError: true,
        disableCardcontent: true,
        errorResponse: error.response.data,
        loading: false
      });
    });

    if (!this.state.getError) {
      if (userData['data']['email']) {
        let userObj = userData['data'];
        this.setState({
          formErrors: {},
          userData: userObj,
          loading: false,
          userFound: true,
          orgAddress: userObj['orgAddress'],
          profileId: profile,
          newUserData: {},
          disableCardcontent: false,
          postError: false,
          getError: false
        });
      } else {
        this.setState({
          userFound: false,
          disableCardcontent: true,
          loading: false
        });
      }
    }
  }

  wasDataUpdated(oldData, newData) {
    return !Object.keys(newData).every(
      key => oldData.hasOwnProperty(key) && oldData[key] === newData[key]
    );
  }

  async updateProfile(e) {
    e.preventDefault();
    const { profileId, newUserData, userData } = this.state;

    this.setState({ loading: true });
    if (this.wasDataUpdated(userData, newUserData)) {
      const postObject = { profileId, newUserData, userData };
      const updateResponse = await axios
        .post('/profile_update/update_profile', postObject)
        .catch(error => {
          if (error.response) {
            this.setState({
              loading: false,
              postError: true,
              disableCardcontent: true,
              errorResponse: error.response.data
            });
          }
        });
      if (updateResponse && updateResponse.status === 200) {
        this.setState({
          loading: false,
          disableCardcontent: true,
          cardContentText: 'Your profile information has been updated.'
        });
      } else {
        this.setState({
          loading: false,
          postError: true
        });
      }
    } else {
      this.setState({
        loading: false,
        disableCardcontent: true,
        cardContentText: 'There was no change in your profile information.'
      });
      setTimeout(() => {
        this.setState({
          loading: true,
          disableCardcontent: false
        });
        this.prepareProfile();
      }, 5000);
    }
  }

  reloadProfile() {
    this.setState({
      formErrors: {},
      getError: false,
      loading: true,
      postError: false,
      disableCardcontent: false
    });
    this.prepareProfile();
  }

  cancelUpdate() {
    this.setState({
      cardContentText: 'Your request has been cancelled.',
      disableCardcontent: true
    });
    setTimeout(() => {
      this.setState({
        loading: true,
        disableCardcontent: false
      });
      this.prepareProfile();
    }, 5000);
  }

  handleUserDataChange(event, field) {
    const { newUserData } = this.state;
    const newValue = event.target.value.trim();
    newUserData[field] = newValue;
    this.validate({ [field]: newValue });
    this.setState({ newUserData });
  }

  validate(fieldValues) {
    let { formErrors, updateDisabled } = this.state;
    if ('firstName' in fieldValues)
      if (
        fieldValues.firstName &&
        fieldValues.firstName.length > 0 &&
        fieldValues.firstName.length <= 40
      ) {
        formErrors.firstName = '';
      } else {
        formErrors.firstName =
          'This is a required field with a 40 character max.';
      }
    if ('lastName' in fieldValues)
      if (
        fieldValues.lastName &&
        fieldValues.lastName.length > 0 &&
        fieldValues.lastName.length <= 80
      ) {
        formErrors.lastName = '';
      } else {
        formErrors.lastName =
          'This is a required field with an 80 character max.';
      }
    if ('email' in fieldValues)
      if (
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          fieldValues.email
        ) &&
        fieldValues.email.length > 0 &&
        fieldValues.email.length <= 80
      ) {
        formErrors.email = '';
      } else {
        formErrors.email = 'Email is not valid.';
      }
    updateDisabled = Object.keys(formErrors).some(
      key => formErrors[key] !== ''
    );
    this.setState({
      formErrors,
      updateDisabled
    });
  }

  render() {
    const { classes } = this.props;
    const {
      loading,
      userData,
      getError,
      postError,
      userFound,
      orgAddress,
      updateDisabled,
      disableCardcontent,
      formErrors
    } = this.state;
    if (userData.firstName) {
      document.title = `${userData.firstName}'s Profile Preferences`;
    } else {
      document.title = 'Profile Preferences';
    }

    return (
      <MuiThemeProvider theme={themeDark}>
        <CssBaseline />
        <div>
          {loading ? (
            <div className={classes.loadingEllipsisWrapper}>
              <LoadingEllipsis />
            </div>
          ) : (
            <form>
              <Card className={classes.card}>
                <CardContent className={classes.content}>
                  {!disableCardcontent ? (
                    <div>
                      <Typography
                        className={classes.heading}
                        variant="h1"
                        gutterBottom
                        align="center"
                      >
                        UPDATE PROFILE
                      </Typography>
                      <Divider className={classes.divider} light fullWidth />
                    </div>
                  ) : null}
                  {disableCardcontent && !userFound ? (
                    <div>
                      <WarningIcon
                        className={classes.warningIconSingleLine}
                        style={{ color: 'darkRed' }}
                      />
                      <Typography className={classes.errorText}>
                        Requested user not found
                      </Typography>
                    </div>
                  ) : disableCardcontent && getError ? (
                    <div>
                      <WarningIcon
                        className={classes.warningIconSingleLine}
                        style={{ color: 'darkRed' }}
                      />
                      <Typography className={classes.errorText}>
                        We were unable to load your profile information. Please
                        try again.
                      </Typography>
                    </div>
                  ) : disableCardcontent && postError ? (
                    <div>
                      <WarningIcon
                        className={classes.warningIconMultiLine}
                        style={{ color: 'darkRed' }}
                      />
                      <Typography className={classes.errorText}>
                        {userData.replyEmail.length > 0 ? (
                          <span>
                            We were unable to update your information. Please
                            contact{' '}
                            <a
                              className={classes.link}
                              href={'mailto:' + userData.replyEmail}
                            >
                              {userData.replyVanity.length > 0
                                ? userData.replyVanity
                                : userData.replyEmail}
                            </a>{' '}
                            for assistance.
                          </span>
                        ) : (
                          <span>
                            We were unable to update your information.
                          </span>
                        )}
                      </Typography>
                      <Typography className={classes.errorText}>
                        <span
                          className={classes.link}
                          onClick={() => this.reloadProfile()}
                        >
                          Go back to Update Profile form.
                        </span>
                      </Typography>
                    </div>
                  ) : this.state.disableCardcontent ? (
                    <div>
                      <CheckTwoToneIcon
                        className={classes.checkIcon}
                        style={{ backgroundColor: green[500] }}
                      />
                      <Typography className={classes.thankyoutext}>
                        Thank you!
                      </Typography>
                      <Typography className={classes.cardContentText}>
                        {this.state.cardContentText}
                      </Typography>
                    </div>
                  ) : (
                    <div>
                      <Typography
                        style={{
                          marginTop: 20,
                          marginBottom: 20,
                          color: '#666666'
                        }}
                        variant="body1"
                      >
                        Changes to your First Name, Last Name, or Email will
                        update your contact information. Please ensure the
                        information provided is correct.
                      </Typography>
                      <Typography
                        style={{
                          marginBottom: 2,
                          fontWeight: 'bold',
                          color: '#666666'
                        }}
                        variant="body1"
                      >
                        First Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        required
                        type="text"
                        id="outlined-full-width-fname"
                        inputProps={{
                          'aria-label': 'first name',
                        }}
                        style={{
                          marginBottom: 24,
                          marginTop: 0,
                          color: '#666666'
                        }}
                        defaultValue={userData['firstName']}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{
                          shrink: true
                        }}
                        variant="outlined"
                        inputRef={input => input && input.focus()}
                        onChange={event =>
                          this.handleUserDataChange(event, 'firstName')
                        }
                        {...(formErrors.firstName && {
                          error: true,
                          helperText: formErrors.firstName
                        })}
                      />
                      <Typography
                        style={{
                          marginBottom: 2,
                          fontWeight: 'bold',
                          color: '#666666'
                        }}
                        variant="body1"
                      >
                        Last Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        required
                        type="text"
                        id="outlined-full-width-lname"
                        inputProps={{
                          'aria-label': 'last name',
                        }}
                        style={{
                          marginBottom: 24,
                          marginTop: 0,
                          color: '#666666'
                        }}
                        defaultValue={userData['lastName']}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{
                          shrink: true
                        }}
                        variant="outlined"
                        onChange={event =>
                          this.handleUserDataChange(event, 'lastName')
                        }
                        {...(formErrors.lastName && {
                          error: true,
                          helperText: formErrors.lastName
                        })}
                      />
                      <Typography
                        style={{
                          marginBottom: 2,
                          fontWeight: 'bold',
                          color: '#666666'
                        }}
                        variant="body1"
                      >
                        Email<span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        required
                        type="email"
                        id="outlined-full-width-email"
                        inputProps={{
                          'aria-label': 'email',
                        }}
                        style={{
                          marginBottom: 24,
                          marginTop: 0,
                          color: '#666666'
                        }}
                        defaultValue={userData['email']}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{
                          shrink: true
                        }}
                        variant="outlined"
                        onChange={event =>
                          this.handleUserDataChange(event, 'email')
                        }
                        {...(formErrors.email && {
                          error: true,
                          helperText: formErrors.email
                        })}
                      />
                      <Divider className={classes.divider} light fullWidth />
                      <div>
                        <CancelButton
                          variant="contained"
                          onClick={() => this.cancelUpdate()}
                        >
                          CANCEL
                        </CancelButton>
                        <UpdateButton
                          disabled={updateDisabled}
                          type="submit"
                          variant="contained"
                          onClick={e => this.updateProfile(e)}
                        >
                          UPDATE
                        </UpdateButton>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              {orgAddress['city'] &&
              orgAddress['state'] &&
              orgAddress['zipcode'] ? (
                  <div>
                    <Typography variant="caption" className={classes.footertext}>
                      {orgAddress['name']}
                    </Typography>
                    <Typography variant="caption" className={classes.footertext}>
                      {orgAddress['addr1']}
                      {orgAddress['addr1'] && orgAddress['addr2'] &&
                        ', ' + orgAddress['addr2']}
                    </Typography>
                    <Typography variant="caption" className={classes.footertext}>
                      {orgAddress['city']}, {orgAddress['state']}{' '}
                      {orgAddress['zipcode']}
                    </Typography>
                  </div>
                ) : null}
            </form>
          )}
        </div>
      </MuiThemeProvider>
    );
  }
}

ProfileUpdate.propTypes = {
  location: PropTypes.object,
  classes: PropTypes.object.isRequired,
  profile: PropTypes.string.isRequired
};

export default withStyles(styles)(ProfileUpdate);



// WEBPACK FOOTER //
// ./src/client/views/profile_update.js